/**
 * API Service Utility
 * Handles all API calls to the backend
 */

// Normalize API URL: fix common typo "https:/" -> "https://"
const rawApiUrl = process.env.REACT_APP_API_URL || "http://localhost:3030/api";
const API_BASE_URL = typeof rawApiUrl === "string" && rawApiUrl.startsWith("https:/") && !rawApiUrl.startsWith("https://")
  ? rawApiUrl.replace(/^https:\/(?!\/)/, "https://")
  : rawApiUrl;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem("token");
};

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const url = `${API_BASE_URL}${endpoint}`;

  // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
  const isFormData = options.body instanceof FormData;

  const config = {
    ...options,
    headers: {
      // Only set Content-Type for JSON, not for FormData
      ...(options.body && !isFormData && { "Content-Type": "application/json" }),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Get response as text first to check content type
    const responseText = await response.text();
    const contentType = response.headers.get("content-type");

    // Check if it's an HTML error page (like "Cannot POST /api/auth/login")
    if (
      responseText.includes("<!DOCTYPE html>") ||
      responseText.includes("Cannot POST") ||
      responseText.includes("Cannot GET")
    ) {
      const errorMsg = responseText.match(/<pre>(.*?)<\/pre>/)?.[1] || responseText;
      const backendUrl = API_BASE_URL.replace("/api", "");
      throw new Error(
        `Backend route not found: ${errorMsg}. Please ensure: 1) Backend server is running on ${backendUrl}, 2) The route /api/auth/login exists, 3) CORS is properly configured.`
      );
    }

    // Try to parse as JSON if content type suggests it
    let data;
    if (contentType && contentType.includes("application/json")) {
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(responseText || "Invalid JSON response from server");
      }
    } else {
      throw new Error(responseText || "Invalid response from server");
    }

    if (!response.ok) {
      // Handle different error status codes
      const errorMessage = data.message || data.error || "An error occurred";
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data; // Preserve full error data
      console.error("API Error Response:", {
        status: response.status,
        message: errorMessage,
        data: data,
      });
      throw error;
    }

    return data;
  } catch (error) {
    console.error("API Error:", error);
    console.error("Request URL:", url);
    console.error("API Base URL:", API_BASE_URL);

    // Handle network / CORS / connection errors
    const msg = (error.message || "").toLowerCase();
    if (
      msg.includes("failed to fetch") ||
      msg.includes("networkerror") ||
      msg.includes("network request failed") ||
      msg.includes("load failed") ||
      msg.includes("could not connect")
    ) {
      const backendUrl = API_BASE_URL.replace("/api", "");
      throw new Error(
        `Cannot connect to backend at ${backendUrl}. Start the backend (e.g. \`npm run dev\` in the project root) and ensure CORS allows this app.`
      );
    }

    // If it's already an Error object with message, re-throw it
    if (error.message) {
      throw error;
    }
    // Otherwise, wrap it in an Error
    throw new Error(
      error.message ||
        "Network error. Please check your connection and ensure the backend server is running."
    );
  }
};

// Auth API
export const authAPI = {
  login: async (email, password) => {
    return apiRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },
  resendVerificationEmail: async (email) => {
    return apiRequest("/property-owner/resend-verification", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },
  forgotPassword: async (email) => {
    return apiRequest("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email: email.trim() }),
    });
  },
  resetPassword: async (token, newPassword) => {
    return apiRequest("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, newPassword }),
    });
  },
};

// User API (for staff profile management)
export const userAPI = {
  getProfile: async () => {
    return apiRequest("/users/profile", {
      method: "GET",
    });
  },
  updateProfile: async (formData) => {
    const isFormData = formData instanceof FormData;
    const options = {
      method: "PUT",
      body: isFormData ? formData : JSON.stringify(formData),
    };
    return apiRequest("/users/profile", options);
  },
  changePassword: async (currentPassword, newPassword) => {
    return apiRequest("/users/profile", {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },
};

// Client API
export const clientAPI = {
  register: async (formData) => {
    // formData should be FormData if ID photo is included, or regular object
    const isFormData = formData instanceof FormData;
    const options = {
      method: "POST",
      body: isFormData ? formData : JSON.stringify(formData),
    };
    return apiRequest("/clients/register", options);
  },
  login: async (email, password) => {
    return apiRequest("/clients/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },
  verifyEmail: async (token) => {
    return apiRequest(`/clients/verify/${token}`, {
      method: "GET",
    });
  },
  getProfile: async () => {
    return apiRequest("/clients/profile", {
      method: "GET",
    });
  },
  updateProfile: async (formData) => {
    const isFormData = formData instanceof FormData;
    const options = {
      method: "PUT",
      body: isFormData ? formData : JSON.stringify(formData),
    };
    return apiRequest("/clients/profile", options);
  },
  changePassword: async (currentPassword, newPassword) => {
    return apiRequest("/clients/change-password", {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },
};

// Super Admin API
export const superAdminAPI = {
  // Property Owners
  getPendingPropertyOwners: async () => {
    const response = await apiRequest("/admin/property-owners/pending");
    // Backend returns: { message, count, owners }
    return { ...response, data: response.owners || [] };
  },
  getAllPropertyOwners: async (status) => {
    const query = status ? `?status=${status}` : "";
    const response = await apiRequest(`/admin/property-owners${query}`);
    // Backend returns: { message, count, owners }
    return { ...response, data: response.owners || [] };
  },
  getPropertyOwnerById: async (id) => {
    const response = await apiRequest(`/admin/property-owners/${id}`);
    // Backend returns: { message, owner }
    return { ...response, data: response.owner };
  },
  approvePropertyOwner: async (id, data) => {
    try {
      // Handle both old format (notes string) and new format (object with notes and type)
      const requestData =
        typeof data === "string"
          ? { notes: data || "" }
          : { notes: data?.notes || "", type: data?.type || undefined };

      console.log("🔵 API - approvePropertyOwner called with:", { id, data: requestData });
      const options = {
        method: "PATCH",
        body: JSON.stringify(requestData),
      };
      const endpoint = `/admin/property-owners/${id}/approve`;
      const fullUrl = `${API_BASE_URL}${endpoint}`;
      console.log("🔵 API - Full URL:", fullUrl);
      console.log("🔵 API - Request options:", options);
      console.log("🔵 API - Request body:", options.body);
      const response = await apiRequest(endpoint, options);
      console.log("🔵 API - Response received:", response);
      console.log("🔵 API - Response type:", typeof response);
      console.log("🔵 API - Response keys:", Object.keys(response || {}));
      const result = { ...response, success: true };
      console.log("🔵 API - Returning result:", result);
      return result;
    } catch (error) {
      console.error("🔴 API - Error approving property owner:", error);
      console.error("🔴 API - Error details:", {
        message: error.message,
        status: error.status,
        data: error.data,
        stack: error.stack,
      });
      throw error;
    }
  },
  rejectPropertyOwner: async (id, reason) => {
    try {
      console.log("🔴 API - rejectPropertyOwner called with:", { id, reason });
      if (!reason || reason.trim().length === 0) {
        throw new Error("Rejection reason is required");
      }
      const endpoint = `/admin/property-owners/${id}/reject`;
      console.log("🔴 API - Calling endpoint:", endpoint);
      const response = await apiRequest(endpoint, {
        method: "PATCH",
        body: JSON.stringify({ reason }),
      });
      console.log("🔴 API - Response received:", response);
      return { ...response, success: true };
    } catch (error) {
      console.error("🔴 API - Error rejecting property owner:", error);
      console.error("🔴 API - Error details:", {
        message: error.message,
        status: error.status,
        data: error.data,
      });
      throw error;
    }
  },
  suspendPropertyOwner: async (id, reason) => {
    const options = { method: "PATCH" };
    if (reason) {
      options.body = JSON.stringify({ reason });
    }
    const response = await apiRequest(`/admin/property-owners/${id}/suspend`, options);
    return { ...response, success: true };
  },
  deletePropertyOwner: async (id) => {
    try {
      const response = await apiRequest(`/admin/property-owners/${id}`, { method: "DELETE" });
      return { ...response, success: true };
    } catch (error) {
      throw error;
    }
  },
  updatePropertyOwner: async (id, data) => {
    const response = await apiRequest(`/admin/property-owners/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return response;
  },

  // Properties
  getAllProperties: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const query = queryParams ? `?${queryParams}` : "";
    const response = await apiRequest(`/admin/properties${query}`);
    // Backend returns: { message, count, total, properties }
    return { ...response, data: response.properties || [] };
  },
  getPropertiesByStatus: async (status) => {
    const response = await apiRequest(`/admin/properties/status/${status}`);
    // Backend returns: { message, status, count, total, properties }
    return { ...response, data: response.properties || [] };
  },
  getPropertiesReadyForApproval: async () => {
    const response = await apiRequest("/admin/properties/ready-for-approval");
    // Backend returns: { message, count, total, properties }
    return { ...response, data: response.properties || [] };
  },
  getPropertyById: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}`);
    // Backend returns: { message, property }
    return { ...response, data: response.property };
  },
  approveToContracting: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/approve-to-contracting`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  approveToInspectionOffer: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/approve-to-inspection-offer`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  moveToPreparation: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/move-to-preparation`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  moveToLegalization: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/move-to-legalization`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  moveToSecondInspection: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/move-to-second-inspection`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  approveSecondInspection: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/approve-second-inspection`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  approveProperty: async (
    id,
    price,
    profitPercentage,
    notes,
    insurancePrice,
    checkInOutAgentId,
    expectedReadyDate,
    maintenanceCostPercentage,
    maxGuests,
    extraFeesPerPerson,
    earlyCheckInFeePercentage,
    lateCheckOutFeePercentage
  ) => {
    const body = {
      price: parseFloat(price),
      profitPercentage: parseFloat(profitPercentage),
      notes: notes || "",
    };
    if (insurancePrice !== undefined && insurancePrice !== null) {
      body.insurancePrice = parseFloat(insurancePrice);
    }
    if (checkInOutAgentId) {
      body.checkInOutAgentId = checkInOutAgentId;
    }
    if (expectedReadyDate) {
      body.expectedReadyDate = expectedReadyDate;
    }
    if (maintenanceCostPercentage !== undefined && maintenanceCostPercentage !== null) {
      body.maintenanceCostPercentage = parseFloat(maintenanceCostPercentage);
    }
    if (maxGuests !== undefined && maxGuests !== null && maxGuests !== "") {
      body.maxGuests = parseInt(maxGuests, 10);
    }
    if (
      extraFeesPerPerson !== undefined &&
      extraFeesPerPerson !== null &&
      extraFeesPerPerson !== ""
    ) {
      body.extraFeesPerPerson = parseFloat(extraFeesPerPerson);
    }
    if (
      earlyCheckInFeePercentage !== undefined &&
      earlyCheckInFeePercentage !== null &&
      earlyCheckInFeePercentage !== ""
    ) {
      body.earlyCheckInFeePercentage = parseFloat(earlyCheckInFeePercentage);
    }
    if (
      lateCheckOutFeePercentage !== undefined &&
      lateCheckOutFeePercentage !== null &&
      lateCheckOutFeePercentage !== ""
    ) {
      body.lateCheckOutFeePercentage = parseFloat(lateCheckOutFeePercentage);
    }
    const options = {
      method: "PATCH",
      body: JSON.stringify(body),
    };
    const response = await apiRequest(`/admin/properties/${id}/approve`, options);
    return { ...response, success: true };
  },
  rejectProperty: async (id, reason) => {
    const response = await apiRequest(`/admin/properties/${id}/reject`, {
      method: "PATCH",
      body: JSON.stringify({ reason }),
    });
    return { ...response, success: true };
  },
  sendMissingItemsEmail: async (id, payload) => {
    const response = await apiRequest(`/admin/properties/${id}/send-missing-items-email`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return { ...response, success: true };
  },
  activateProperty: async (id, body = null) => {
    const options = { method: "PATCH" };
    if (body && typeof body === "object" && Object.keys(body).length > 0) {
      options.body = JSON.stringify(body);
      options.headers = { "Content-Type": "application/json" };
    }
    const response = await apiRequest(`/admin/properties/${id}/activate`, options);
    return { ...response, success: true };
  },
  suspendProperty: async (id, reason = "") => {
    try {
      const options = { method: "PATCH" };
      if (reason) {
        options.body = JSON.stringify({ reason });
        options.headers = { "Content-Type": "application/json" };
      }
      const response = await apiRequest(`/admin/properties/${id}/suspend`, options);
      return { ...response, success: true };
    } catch (error) {
      console.error("Error suspending property:", error);
      throw error;
    }
  },
  setPropertyMaintenance: async (id, reason = "") => {
    try {
      const options = { method: "PATCH" };
      if (reason) {
        options.body = JSON.stringify({ reason });
        options.headers = { "Content-Type": "application/json" };
      }
      const response = await apiRequest(`/admin/properties/${id}/maintenance`, options);
      return { ...response, success: true };
    } catch (error) {
      console.error("Error setting property to maintenance:", error);
      throw error;
    }
  },
  deleteProperty: async (id) => {
    try {
      const response = await apiRequest(`/admin/properties/${id}`, {
        method: "DELETE",
      });
      return { ...response, success: true };
    } catch (error) {
      console.error("Error deleting property:", error);
      throw error;
    }
  },
  updatePropertyDetails: async (id, data, files = null) => {
    // If files are provided, use FormData; otherwise use JSON
    const isFormData =
      files !== null &&
      ((files.images && files.images.length > 0) ||
        (files.videos && files.videos.length > 0) ||
        (files.legalPapers && files.legalPapers.length > 0) ||
        (files.buildingImages && files.buildingImages.length > 0) ||
        (files.officialDocuments && files.officialDocuments.length > 0) ||
        (files.livingRoomPhoto && files.livingRoomPhoto.length > 0) ||
        (files.roomPhotos && files.roomPhotos.length > 0) ||
        (files.bathroomPhotos && files.bathroomPhotos.length > 0) ||
        (files.balconyPhotos && files.balconyPhotos.length > 0) ||
        (files.propertyOwnerId && files.propertyOwnerId.length > 0) ||
        (files.rentalOrOwnershipContract && files.rentalOrOwnershipContract.length > 0) ||
        (files.powerOfAttorney && files.powerOfAttorney.length > 0));

    let body;
    let headers = {};

    if (isFormData) {
      const formData = new FormData();

      // Add all data fields to FormData
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined && data[key] !== null) {
          if (Array.isArray(data[key])) {
            // For arrays, stringify them
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        }
      });

      // Add files to FormData
      if (files.images) {
        files.images.forEach((file) => formData.append("images", file));
      }
      if (files.videos) {
        files.videos.forEach((file) => formData.append("videos", file));
      }
      if (files.legalPapers) {
        files.legalPapers.forEach((file) => formData.append("legalPapers", file));
      }
      if (files.buildingImages) {
        files.buildingImages.forEach((file) => formData.append("buildingImages", file));
      }
      if (files.officialDocuments) {
        files.officialDocuments.forEach((file) => formData.append("officialDocuments", file));
      }
      if (files.livingRoomPhoto) {
        files.livingRoomPhoto.forEach((file) => formData.append("livingRoomPhoto", file));
      }
      if (files.roomPhotos) {
        files.roomPhotos.forEach((file) => formData.append("roomPhotos", file));
      }
      if (files.bathroomPhotos) {
        files.bathroomPhotos.forEach((file) => formData.append("bathroomPhotos", file));
      }
      if (files.balconyPhotos) {
        files.balconyPhotos.forEach((file) => formData.append("balconyPhotos", file));
      }
      if (files.propertyOwnerId) {
        files.propertyOwnerId.forEach((file) => formData.append("propertyOwnerId", file));
      }
      if (files.rentalOrOwnershipContract) {
        files.rentalOrOwnershipContract.forEach((file) =>
          formData.append("rentalOrOwnershipContract", file)
        );
      }
      if (files.powerOfAttorney) {
        files.powerOfAttorney.forEach((file) => formData.append("powerOfAttorney", file));
      }

      body = formData;
      // Don't set Content-Type header for FormData - browser will set it with boundary
    } else {
      body = JSON.stringify(data);
      headers["Content-Type"] = "application/json";
    }

    const response = await apiRequest(`/admin/properties/${id}`, {
      method: "PATCH",
      body,
      headers,
    });
    return response;
  },
  quickUpdatePropertyPricing: async (id, data) => {
    const response = await apiRequest(`/admin/properties/${id}/pricing`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  },
  updatePropertyCheckInOutAgent: async (id, checkInOutAgentId) => {
    const response = await apiRequest(`/admin/properties/${id}/check-in-out-agent`, {
      method: "PATCH",
      body: JSON.stringify({ checkInOutAgentId }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return { ...response, success: true };
  },

  // User Management
  getAvailableRoles: async () => {
    const response = await apiRequest("/admin/roles");
    // Backend returns: { message, count, roles }
    return { ...response, data: response.roles || [] };
  },
  createUserAccount: async (userData) => {
    // userData can be FormData (for file uploads) or regular object
    const isFormData = userData instanceof FormData;
    const options = {
      method: "POST",
      body: isFormData ? userData : JSON.stringify(userData),
    };
    const response = await apiRequest("/admin/users", options);
    return { ...response, success: true, data: response.user };
  },
  getAllUsers: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const query = queryParams ? `?${queryParams}` : "";
    const response = await apiRequest(`/admin/users${query}`);
    // Backend returns: { message, count, total, users }
    return { ...response, data: response.users || [] };
  },
  getUserById: async (id) => {
    const response = await apiRequest(`/admin/users/${id}`);
    // Backend returns: { message, user }
    return { ...response, data: response.user };
  },
  updateUserAccount: async (id, userData) => {
    const isFormData = userData instanceof FormData;
    const options = {
      method: "PATCH",
      body: isFormData ? userData : JSON.stringify(userData),
    };
    if (!isFormData) {
      options.headers = {
        "Content-Type": "application/json",
      };
    }
    const response = await apiRequest(`/admin/users/${id}`, options);
    return { ...response, success: true, data: response.user };
  },
  deactivateUserAccount: async (id) => {
    const response = await apiRequest(`/admin/users/${id}/deactivate`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  activateUserAccount: async (id) => {
    const response = await apiRequest(`/admin/users/${id}/activate`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  deleteUserAccount: async (id) => {
    const response = await apiRequest(`/admin/users/${id}`, {
      method: "DELETE",
    });
    return { ...response, success: true };
  },

  // Clients (Client table - website users who book)
  getAllClients: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const query = queryParams ? `?${queryParams}` : "";
    const response = await apiRequest(`/admin/clients${query}`);
    return { ...response, data: response.clients || [] };
  },

  // Reservations
  getAllReservations: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const query = queryParams ? `?${queryParams}` : "";
    const response = await apiRequest(`/reservations${query}`);
    // Backend returns: { message, reservations, total, page, limit }
    return { ...response, data: response.reservations || [] };
  },
  getReservationById: async (id) => {
    const response = await apiRequest(`/reservations/${id}`);
    return { ...response, data: response.reservation };
  },
  getHousekeepingTasks: async () => {
    const response = await apiRequest("/reservations/housekeeping/tasks");
    return { ...response, data: response.tasks || [] };
  },
  getFinancialSummary: async () => {
    // This will aggregate financial data from all reservations
    const response = await apiRequest("/api/reservations/financial-summary");
    return response;
  },
  getReservationStatistics: async () => {
    const response = await apiRequest("/reservations/statistics");
    return { ...response, data: response.statistics };
  },
  getItemsToProcess: async () => {
    const response = await apiRequest("/reservations/items-to-process");
    return { ...response, reservations: response.reservations || [] };
  },
  getItemsReport: async () => {
    const response = await apiRequest("/reservations/items-report");
    return { ...response, report: response.report || [], total: response.total || 0 };
  },
  markMissingItemReplaced: async (reservationId, index) => {
    const response = await apiRequest(
      `/reservations/${reservationId}/missing-items/${index}/replace`,
      {
        method: "PATCH",
      }
    );
    return response;
  },
  markDamagedItemMaintained: async (reservationId, index) => {
    const response = await apiRequest(
      `/reservations/${reservationId}/damaged-items/${index}/maintain`,
      {
        method: "PATCH",
      }
    );
    return response;
  },
  submitDamagedItemsCosts: async (reservationId, costs) => {
    const response = await apiRequest(`/reservations/${reservationId}/damaged-items-costs`, {
      method: "PATCH",
      body: JSON.stringify({ costs }),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
  // Property Closure Requests (Admin & Super Admin)
  getAllClosureRequests: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const query = queryParams ? `?${queryParams}` : "";
    const response = await apiRequest(`/admin/closure-requests${query}`);
    return { ...response, requests: response.requests || [], count: response.count || 0 };
  },
  getClosureRequestById: async (id) => {
    const response = await apiRequest(`/admin/closure-requests/${id}`);
    return {
      ...response,
      request: response.request || null,
      validationResults: response.validationResults || null,
    };
  },
  approveClosureRequest: async (id, adminNotes = "") => {
    const response = await apiRequest(`/admin/closure-requests/${id}/approve`, {
      method: "PATCH",
      body: JSON.stringify({ adminNotes }),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
  rejectClosureRequest: async (id, adminNotes) => {
    if (!adminNotes || !adminNotes.trim()) {
      throw new Error("Admin notes are required when rejecting a closure request");
    }
    const response = await apiRequest(`/admin/closure-requests/${id}/reject`, {
      method: "PATCH",
      body: JSON.stringify({ adminNotes }),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },

  // Early Checkout Settings & Requests (Super Admin only)
  getEarlyCheckoutSettings: async () => {
    const response = await apiRequest("/admin/early-checkout-settings");
    return { ...response, ...response };
  },
  updateEarlyCheckoutSettings: async (data) => {
    const response = await apiRequest("/admin/early-checkout-settings", {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
  getEarlyCheckoutRequests: async (status) => {
    const query = status ? `?status=${encodeURIComponent(status)}` : "";
    const response = await apiRequest(`/admin/early-checkout-requests${query}`);
    return { ...response, requests: response.requests || [] };
  },
  approveEarlyCheckoutRequest: async (reservationId, body = {}) => {
    const response = await apiRequest(`/admin/early-checkout-requests/${reservationId}/approve`, {
      method: "PATCH",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
  rejectEarlyCheckoutRequest: async (reservationId, rejectionReason = "") => {
    const response = await apiRequest(`/admin/early-checkout-requests/${reservationId}/reject`, {
      method: "PATCH",
      body: JSON.stringify({ rejectionReason }),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },

  // Late Checkout Settings (Super Admin only)
  getLateCheckoutSettings: async () => {
    const response = await apiRequest("/admin/late-checkout-settings");
    return { ...response };
  },
  updateLateCheckoutSettings: async (data) => {
    const response = await apiRequest("/admin/late-checkout-settings", {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },

  getCancellationPolicySettings: async () => {
    const response = await apiRequest("/admin/cancellation-policy-settings");
    return { ...response };
  },
  updateCancellationPolicySettings: async (data) => {
    const response = await apiRequest("/admin/cancellation-policy-settings", {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },

  // Amenities Management
  getAllAmenities: async () => {
    const response = await apiRequest("/admin/amenities");
    return { ...response, data: response.data || [] };
  },
  createAmenity: async (data) => {
    const response = await apiRequest("/admin/amenities", {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    });
    return { ...response, success: true, data: response.data };
  },
  updateAmenity: async (id, data) => {
    const response = await apiRequest(`/admin/amenities/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    });
    return { ...response, success: true, data: response.data };
  },
  deleteAmenity: async (id) => {
    const response = await apiRequest(`/admin/amenities/${id}`, {
      method: "DELETE",
    });
    return { ...response, success: true };
  },

  // Criteria Management
  getAllCriteria: async () => {
    const response = await apiRequest("/admin/criteria");
    return { ...response, data: response.data || [] };
  },
  createCriteria: async (data) => {
    const response = await apiRequest("/admin/criteria", {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    });
    return { ...response, success: true, data: response.data };
  },
  updateCriteria: async (id, data) => {
    const response = await apiRequest(`/admin/criteria/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    });
    return { ...response, success: true, data: response.data };
  },
  deleteCriteria: async (id) => {
    const response = await apiRequest(`/admin/criteria/${id}`, {
      method: "DELETE",
    });
    return { ...response, success: true };
  },
};

// Admin API
export const adminAPI = {
  getPendingProperties: async () => {
    const response = await apiRequest("/admin/properties/pending");
    // Backend returns: { message, count, properties }
    return { ...response, properties: response.properties || [], data: response.properties || [] };
  },
  getAllProperties: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const query = queryParams ? `?${queryParams}` : "";
    const response = await apiRequest(`/admin/properties${query}`);
    // Backend returns: { message, count, properties }
    return { ...response, properties: response.properties || [], data: response.properties || [] };
  },
  getPropertyById: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}`);
    // Backend returns: { message, property }
    return { ...response, property: response.property, data: response.property };
  },
  updatePropertyInsurancePrice: async (id, insurancePrice) => {
    const response = await apiRequest(`/admin/properties/${id}/insurance-price`, {
      method: "PATCH",
      body: JSON.stringify({ insurancePrice }),
    });
    return response;
  },
  updatePropertyCheckInOutAgent: async (id, checkInOutAgentId) => {
    const response = await apiRequest(`/admin/properties/${id}/check-in-out-agent`, {
      method: "PATCH",
      body: JSON.stringify({ checkInOutAgentId }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return { ...response, success: true };
  },
  updatePropertyDetails: async (id, data, files = null) => {
    // If files are provided, use FormData; otherwise use JSON
    const isFormData =
      files !== null &&
      ((files.images && files.images.length > 0) ||
        (files.videos && files.videos.length > 0) ||
        (files.legalPapers && files.legalPapers.length > 0) ||
        (files.buildingImages && files.buildingImages.length > 0) ||
        (files.officialDocuments && files.officialDocuments.length > 0) ||
        (files.livingRoomPhoto && files.livingRoomPhoto.length > 0) ||
        (files.roomPhotos && files.roomPhotos.length > 0) ||
        (files.bathroomPhotos && files.bathroomPhotos.length > 0) ||
        (files.balconyPhotos && files.balconyPhotos.length > 0) ||
        (files.propertyOwnerId && files.propertyOwnerId.length > 0) ||
        (files.rentalOrOwnershipContract && files.rentalOrOwnershipContract.length > 0) ||
        (files.powerOfAttorney && files.powerOfAttorney.length > 0));

    let body;
    let headers = {};

    if (isFormData) {
      const formData = new FormData();

      // Add all data fields to FormData
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined && data[key] !== null) {
          if (Array.isArray(data[key])) {
            // For arrays, stringify them
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        }
      });

      // Add files to FormData
      if (files.images) {
        files.images.forEach((file) => formData.append("images", file));
      }
      if (files.videos) {
        files.videos.forEach((file) => formData.append("videos", file));
      }
      if (files.legalPapers) {
        files.legalPapers.forEach((file) => formData.append("legalPapers", file));
      }
      if (files.buildingImages) {
        files.buildingImages.forEach((file) => formData.append("buildingImages", file));
      }
      if (files.officialDocuments) {
        files.officialDocuments.forEach((file) => formData.append("officialDocuments", file));
      }
      if (files.livingRoomPhoto) {
        files.livingRoomPhoto.forEach((file) => formData.append("livingRoomPhoto", file));
      }
      if (files.roomPhotos) {
        files.roomPhotos.forEach((file) => formData.append("roomPhotos", file));
      }
      if (files.bathroomPhotos) {
        files.bathroomPhotos.forEach((file) => formData.append("bathroomPhotos", file));
      }
      if (files.balconyPhotos) {
        files.balconyPhotos.forEach((file) => formData.append("balconyPhotos", file));
      }
      if (files.propertyOwnerId) {
        files.propertyOwnerId.forEach((file) => formData.append("propertyOwnerId", file));
      }
      if (files.rentalOrOwnershipContract) {
        files.rentalOrOwnershipContract.forEach((file) =>
          formData.append("rentalOrOwnershipContract", file)
        );
      }
      if (files.powerOfAttorney) {
        files.powerOfAttorney.forEach((file) => formData.append("powerOfAttorney", file));
      }

      body = formData;
      // Don't set Content-Type header for FormData - browser will set it with boundary
    } else {
      body = JSON.stringify(data);
      headers["Content-Type"] = "application/json";
    }

    const response = await apiRequest(`/admin/properties/${id}`, {
      method: "PATCH",
      body,
      headers,
    });
    return response;
  },
  quickUpdatePropertyPricing: async (id, data) => {
    const response = await apiRequest(`/admin/properties/${id}/pricing`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  },
  getAvailableInspectors: async () => {
    const response = await apiRequest("/admin/inspectors");
    // Backend returns: { message, count, inspectors }
    return { ...response, inspectors: response.inspectors || [], data: response.inspectors || [] };
  },
  getAvailableCheckInOutAgents: async () => {
    const response = await apiRequest("/admin/check-in-out-agents");
    // Backend returns: { message, agents }
    return { ...response, agents: response.agents || [] };
  },
  getInspectorCalendar: async (inspectorId) => {
    const response = await apiRequest(`/admin/inspectors/${inspectorId}/calendar`);
    // Backend returns: { message, inspector, inspections }
    return response;
  },
  getInspectorAnalytics: async (inspectorId) => {
    const response = await apiRequest(`/admin/inspectors/${inspectorId}/analytics`);
    // Backend returns: { message, inspector, analytics }
    return response;
  },
  getCheckInOutAgentAnalytics: async (agentId) => {
    const response = await apiRequest(`/admin/check-in-out-agents/${agentId}/analytics`);
    // Backend returns: { message, agent, analytics }
    return response;
  },
  getOperationsTeam: async () => {
    const response = await apiRequest("/admin/operations-team");
    // Backend returns: { message, inspectors: { count, members }, checkInOutAgents: { count, members } }
    return {
      ...response,
      inspectors: response.inspectors?.members || response.inspectors || [],
      agents: response.checkInOutAgents?.members || response.checkInOutAgents || [],
      data: {
        inspectors: response.inspectors?.members || response.inspectors || [],
        agents: response.checkInOutAgents?.members || response.checkInOutAgents || [],
      },
    };
  },
  assignInspector: async (propertyId, inspectorData) => {
    return apiRequest(`/admin/properties/${propertyId}/assign-inspector`, {
      method: "PATCH",
      body: JSON.stringify(inspectorData),
    });
  },
  getInspectionResults: async () => {
    const response = await apiRequest("/admin/properties/inspections/results");
    // Backend returns: { message, count, properties }
    return { ...response, properties: response.properties || [], data: response.properties || [] };
  },
  rejectProperty: async (propertyId, reason) => {
    return apiRequest(`/admin/properties/${propertyId}/reject`, {
      method: "PATCH",
      body: JSON.stringify({ reason }),
    });
  },
  approveToInspectionOffer: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/approve-to-inspection-offer`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  approveToContracting: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/approve-to-contracting`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  moveToPreparation: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/move-to-preparation`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  moveToLegalization: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/move-to-legalization`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  moveToSecondInspection: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/move-to-second-inspection`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  approveSecondInspection: async (id) => {
    const response = await apiRequest(`/admin/properties/${id}/approve-second-inspection`, {
      method: "PATCH",
    });
    return { ...response, success: true };
  },
  approveProperty: async (
    id,
    price,
    profitPercentage,
    notes,
    insurancePrice,
    checkInOutAgentId,
    expectedReadyDate,
    maintenanceCostPercentage,
    maxGuests,
    extraFeesPerPerson,
    earlyCheckInFeePercentage,
    lateCheckOutFeePercentage
  ) => {
    const body = {
      price: parseFloat(price),
      profitPercentage: parseFloat(profitPercentage),
      notes: notes || "",
    };
    if (insurancePrice !== undefined && insurancePrice !== null)
      body.insurancePrice = parseFloat(insurancePrice);
    if (checkInOutAgentId) body.checkInOutAgentId = checkInOutAgentId;
    if (expectedReadyDate) body.expectedReadyDate = expectedReadyDate;
    if (maintenanceCostPercentage !== undefined && maintenanceCostPercentage !== null)
      body.maintenanceCostPercentage = parseFloat(maintenanceCostPercentage);
    if (maxGuests !== undefined && maxGuests !== null && maxGuests !== "")
      body.maxGuests = parseInt(maxGuests, 10);
    if (
      extraFeesPerPerson !== undefined &&
      extraFeesPerPerson !== null &&
      extraFeesPerPerson !== ""
    )
      body.extraFeesPerPerson = parseFloat(extraFeesPerPerson);
    if (
      earlyCheckInFeePercentage !== undefined &&
      earlyCheckInFeePercentage !== null &&
      earlyCheckInFeePercentage !== ""
    )
      body.earlyCheckInFeePercentage = parseFloat(earlyCheckInFeePercentage);
    if (
      lateCheckOutFeePercentage !== undefined &&
      lateCheckOutFeePercentage !== null &&
      lateCheckOutFeePercentage !== ""
    )
      body.lateCheckOutFeePercentage = parseFloat(lateCheckOutFeePercentage);
    const response = await apiRequest(`/admin/properties/${id}/approve`, {
      method: "PATCH",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    });
    return { ...response, success: true };
  },
  sendMissingItemsEmail: async (id, payload) => {
    const response = await apiRequest(`/admin/properties/${id}/send-missing-items-email`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return { ...response, success: true };
  },
  activateProperty: async (id, body = null) => {
    const options = { method: "PATCH" };
    if (body && typeof body === "object" && Object.keys(body).length > 0) {
      options.body = JSON.stringify(body);
      options.headers = { "Content-Type": "application/json" };
    }
    const response = await apiRequest(`/admin/properties/${id}/activate`, options);
    return { ...response, success: true };
  },
  // Property Owners
  getAllPropertyOwners: async (status) => {
    const query = status ? `?status=${status}` : "";
    const response = await apiRequest(`/admin/property-owners${query}`);
    // Backend returns: { message, count, owners }
    return { ...response, data: response.owners || [], owners: response.owners || [] };
  },
  approvePropertyOwner: async (id, data) => {
    try {
      // Handle both old format (notes string) and new format (object with notes and type)
      const requestData =
        typeof data === "string"
          ? { notes: data || "" }
          : { notes: data?.notes || "", type: data?.type || undefined };

      console.log("🔵 API - approvePropertyOwner called with:", { id, data: requestData });
      const options = {
        method: "PATCH",
        body: JSON.stringify(requestData),
      };
      const endpoint = `/admin/property-owners/${id}/approve`;
      const response = await apiRequest(endpoint, options);
      console.log("🔵 API - Response received:", response);
      const result = { ...response, success: true };
      return result;
    } catch (error) {
      console.error("🔴 API - Error approving property owner:", error);
      throw error;
    }
  },
  suspendPropertyOwner: async (id, reason) => {
    const options = { method: "PATCH" };
    if (reason) {
      options.body = JSON.stringify({ reason });
    }
    const response = await apiRequest(`/admin/property-owners/${id}/suspend`, options);
    return { ...response, success: true };
  },
  updatePropertyOwner: async (id, data) => {
    const response = await apiRequest(`/admin/property-owners/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return response;
  },

  // Package Management
  getAllPackages: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const query = queryParams ? `?${queryParams}` : "";
    const response = await apiRequest(`/admin/packages${query}`);
    return { ...response, data: response.packages || [] };
  },
  getPackageById: async (id) => {
    const response = await apiRequest(`/admin/packages/${id}`);
    return { ...response, data: response.package };
  },
  createPackage: async (data) => {
    const response = await apiRequest("/admin/packages", {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  },
  updatePackage: async (id, data) => {
    const response = await apiRequest(`/admin/packages/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  },
  deletePackage: async (id) => {
    const response = await apiRequest(`/admin/packages/${id}`, {
      method: "DELETE",
    });
    return response;
  },

  // Promo Code Management
  getAllPromoCodes: async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const query = queryParams ? `?${queryParams}` : "";
    const response = await apiRequest(`/admin/promo-codes${query}`);
    return { ...response, data: response.promoCodes || [] };
  },
  getPromoCodeById: async (id) => {
    const response = await apiRequest(`/admin/promo-codes/${id}`);
    return { ...response, data: response.promoCode };
  },
  getPromoCodeByCode: async (code) => {
    const response = await apiRequest(`/admin/promo-codes/code/${code}`);
    return { ...response, data: response.promoCode };
  },
  createPromoCode: async (data) => {
    const response = await apiRequest("/admin/promo-codes", {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  },
  updatePromoCode: async (id, data) => {
    const response = await apiRequest(`/admin/promo-codes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  },
  deletePromoCode: async (id) => {
    const response = await apiRequest(`/admin/promo-codes/${id}`, {
      method: "DELETE",
    });
    return response;
  },
};

// Operations/Inspector API
export const inspectorAPI = {
  getAssignedInspections: async (status) => {
    const query = status ? `?status=${status}` : "";
    return apiRequest(`/operations/inspections${query}`);
  },
  getInspectionById: async (id) => {
    return apiRequest(`/operations/inspections/${id}`);
  },
  submitInspectionResults: async (inspectionId, formData) => {
    return apiRequest(`/operations/inspections/${inspectionId}/submit`, {
      method: "POST",
      body: formData, // FormData with photos and results
    });
  },
};

// Property Owner API
export const propertyOwnerAPI = {
  getMyProperties: async () => {
    const response = await apiRequest("/property/owner/my-properties");
    // Backend returns: { message, count, properties }
    return { ...response, data: response.properties || [] };
  },
  getProfile: async () => {
    const response = await apiRequest("/property-owner/profile");
    // Backend returns: { owner }
    return { ...response, data: response.owner };
  },
  updateProfile: async (formData) => {
    const response = await apiRequest("/property-owner/update-profile", {
      method: "PUT",
      body: formData, // FormData with files and form fields
    });
    return response;
  },
  changePassword: async (currentPassword, newPassword) => {
    return apiRequest("/property-owner/update-credentials", {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },
  requestDisableProperty: async (propertyId, reason) => {
    const response = await apiRequest(`/property/${propertyId}/request-disable`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
    return response;
  },
  createProperty: async (formData) => {
    const response = await apiRequest("/property/create", {
      method: "POST",
      body: formData, // FormData with files and form fields
    });
    return response;
  },
  // Reservations Calendar
  getReservations: async () => {
    const response = await apiRequest("/property-owner/reservations");
    return { ...response, data: response.reservations || [] };
  },
  getReservationById: async (reservationId) => {
    const response = await apiRequest(`/property-owner/reservations/${reservationId}`);
    return { ...response, data: response.reservation || null };
  },
  // Wallet & Financial
  getWallet: async () => {
    const response = await apiRequest("/property-owner/wallet");
    // Backend returns { message, wallet: {...} }
    return { ...response, data: response.wallet || {}, wallet: response.wallet || {} };
  },
  getTransactions: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.startDate) queryParams.append("startDate", filters.startDate);
    if (filters.endDate) queryParams.append("endDate", filters.endDate);
    if (filters.propertyId) queryParams.append("propertyId", filters.propertyId);
    if (filters.type) queryParams.append("type", filters.type);
    if (filters.page) queryParams.append("page", filters.page);
    if (filters.limit) queryParams.append("limit", filters.limit);

    const query = queryParams.toString();
    const endpoint = `/property-owner/transactions${query ? `?${query}` : ""}`;
    const response = await apiRequest(endpoint);
    return { ...response, data: response.transactions || [] };
  },
  // Amenities & Criteria (for property owner form)
  getAmenities: async () => {
    const response = await apiRequest("/admin/amenities");
    return { ...response, data: response.data || [] };
  },
  getCriteria: async () => {
    const response = await apiRequest("/admin/criteria");
    return { ...response, data: response.data || [] };
  },
  // Property Closure Requests
  getClosureRequests: async () => {
    const response = await apiRequest("/property-owner/closure-requests");
    return { ...response, requests: response.requests || [] };
  },
  createClosureRequest: async (data) => {
    const response = await apiRequest("/property-owner/closure-requests", {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
  cancelClosureRequest: async (requestId) => {
    const response = await apiRequest(`/property-owner/closure-requests/${requestId}/cancel`, {
      method: "PATCH",
    });
    return response;
  },
  // Get Properties (for closure request form)
  getProperties: async () => {
    const response = await apiRequest("/property/owner/my-properties");
    return { ...response, data: response.properties || [] };
  },
  // Contract response (Inspection_Offer: approve / decline / request meeting)
  submitContractResponse: async (propertyId, response) => {
    const res = await apiRequest(`/property-owner/properties/${propertyId}/contract-response`, {
      method: "PATCH",
      body: JSON.stringify({ response }),
      headers: { "Content-Type": "application/json" },
    });
    return { ...res, property: res.property };
  },
  // Full URL for draft contract PDF (backend redirects to Cloudinary; use with fetch + auth to open PDF)
  getDraftContractUrl: (propertyId) => {
    const base = API_BASE_URL.replace(/\/$/, "");
    return `${base}/property-owner/properties/${propertyId}/draft-contract`;
  },
};

// Call Center API (Phase 2: leads, call logs, tasks)
const callCenterBase = "/call-center";
export const callCenterAPI = {
  getOwners: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`${callCenterBase}/owners${q ? `?${q}` : ""}`);
  },
  getOwnerById: (id) => apiRequest(`${callCenterBase}/owners/${id}`),
  getProperties: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`${callCenterBase}/properties${q ? `?${q}` : ""}`);
  },
  getPropertyById: (id) => apiRequest(`${callCenterBase}/properties/${id}`),
  getLeads: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`${callCenterBase}/leads${q ? `?${q}` : ""}`);
  },
  getLeadById: (id) => apiRequest(`${callCenterBase}/leads/${id}`),
  createLead: (data) =>
    apiRequest(`${callCenterBase}/leads`, { method: "POST", body: JSON.stringify(data) }),
  updateLead: (id, data) =>
    apiRequest(`${callCenterBase}/leads/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  claimLead: (id) => apiRequest(`${callCenterBase}/leads/${id}/claim`, { method: "POST" }),
  convertLeadToOwner: (leadId, data) =>
    apiRequest(`${callCenterBase}/leads/${leadId}/convert`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  logLeadCall: (leadId, data) =>
    apiRequest(`${callCenterBase}/leads/${leadId}/calls`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  getLeadCalls: (leadId) => apiRequest(`${callCenterBase}/leads/${leadId}/calls`),
  getLeadCallHistory: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`${callCenterBase}/leads/calls${q ? `?${q}` : ""}`);
  },
  searchOwners: (query) => {
    const q = new URLSearchParams({ query: query || "" }).toString();
    return apiRequest(`${callCenterBase}/owners/search${q ? `?${q}` : ""}`);
  },
  importLeads: (payload) =>
    apiRequest(`${callCenterBase}/leads/import`, { method: "POST", body: JSON.stringify(payload) }),
  importLeadsExcel: (formData) =>
    apiRequest(`${callCenterBase}/leads/import-excel`, { method: "POST", body: formData }),
  getLeadImports: () => apiRequest(`${callCenterBase}/leads/imports`),
  getCallLogs: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`${callCenterBase}/call-logs${q ? `?${q}` : ""}`);
  },
  getCallLogById: (id) => apiRequest(`${callCenterBase}/call-logs/${id}`),
  createCallLog: (data) =>
    apiRequest(`${callCenterBase}/call-logs`, { method: "POST", body: JSON.stringify(data) }),
  getTasks: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`${callCenterBase}/tasks${q ? `?${q}` : ""}`);
  },
  getTasksMe: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`${callCenterBase}/tasks/me${q ? `?${q}` : ""}`);
  },
  getTaskById: (id) => apiRequest(`${callCenterBase}/tasks/${id}`),
  createTask: (data) =>
    apiRequest(`${callCenterBase}/tasks`, { method: "POST", body: JSON.stringify(data) }),
  updateTask: (id, data) =>
    apiRequest(`${callCenterBase}/tasks/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  getFollowUpsStuck: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`${callCenterBase}/follow-ups/stuck${q ? `?${q}` : ""}`);
  },
  getAnalyticsMe: () => apiRequest(`${callCenterBase}/analytics/me`),
  getAnalyticsTeam: () => apiRequest(`${callCenterBase}/analytics/team`),
  getAnalyticsGlobal: () => apiRequest(`${callCenterBase}/analytics/global`),
};

export default apiRequest;
